'use client';

import i18next, { type i18n as I18nInstance, type Resource } from 'i18next';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useRef } from 'react';
import { I18nextProvider as I18nextReactProvider, initReactI18next } from 'react-i18next';

import { DEFAULT_LOCALE, LOCALES, NAME_SPACES, type NameSpace } from '~/universal/constants';

type Props = {
  lng: string;
  resources: Resource;
  children: React.ReactNode;
};

const getLocaleFromPathname = (pathname?: string | null) => {
  if (!pathname) {
    return DEFAULT_LOCALE;
  }

  if (pathname === '/ru' || pathname.startsWith('/ru/')) {
    return LOCALES.RU;
  }

  if (pathname === '/en' || pathname.startsWith('/en/')) {
    return LOCALES.EN;
  }

  return LOCALES.UA;
};

const loadLocaleResources = async (lng: string, namespaces: readonly NameSpace[]) => {
  const entries = await Promise.all(
    namespaces.map(async (ns) => {
      const module = await import(`~/shared/libs/i18n/locales/${lng}/${ns}.json`);
      return [ns, module.default] as const;
    }),
  );

  return Object.fromEntries(entries);
};

export default function I18nProvider({ lng = 'ua', resources, children }: Props) {
  const pathname = usePathname();
  const effectiveLng = useMemo(() => getLocaleFromPathname(pathname) ?? lng, [pathname, lng]);

  const i18nRef = useRef<I18nInstance | null>(null);

  if (!i18nRef.current) {
    const instance = i18next.createInstance();

    void instance.use(initReactI18next).init({
      lng: effectiveLng,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [LOCALES.UA, LOCALES.RU, LOCALES.EN],
      resources,
      interpolation: { escapeValue: false },
    });

    i18nRef.current = instance;
  }

  useEffect(() => {
    const syncLanguage = async () => {
      const i18n = i18nRef.current;
      if (!i18n) return;

      const hasAllNamespaces = NAME_SPACES.every((ns) => i18n.hasResourceBundle(effectiveLng, ns));

      if (!hasAllNamespaces) {
        const localeResources = await loadLocaleResources(effectiveLng, NAME_SPACES);

        for (const [ns, bundle] of Object.entries(localeResources)) {
          if (!i18n.hasResourceBundle(effectiveLng, ns)) {
            i18n.addResourceBundle(effectiveLng, ns, bundle, true, true);
          }
        }
      }

      if (i18n.resolvedLanguage !== effectiveLng) {
        await i18n.changeLanguage(effectiveLng);
      }
    };

    void syncLanguage();
  }, [effectiveLng]);

  return <I18nextReactProvider i18n={i18nRef.current}>{children}</I18nextReactProvider>;
}
